import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Cable,
  Check,
  Clipboard,
  Globe,
  Hash,
  Loader2,
  Pencil,
  Save,
  Waves,
  X,
} from "lucide-react";


import { api, ApiError } from "@/api/client";
import type { HashPolicy, Pool, Service } from "@/api/types";
import {
  DispatchEditor,
  dispatchModeName,
  type DispatchValue,
} from "@/components/DispatchEditor";
import {
  LocalityWeightBar,
  type LocalityWeightRow,
} from "@/components/LocalityWeightBar";
import { Pill } from "@/components/Pill";
import { Section } from "@/components/Section";

interface Props {
  service: Service;
  pools: Pool[];
}

/**
 * Same regex as the controller's `Service.name` Pydantic field
 * (`^[a-z][a-z0-9-]*$`). We mirror it client-side so the user gets
 * instant feedback on bad input rather than waiting for a 422.
 */
const SLUG_REGEX = /^[a-z][a-z0-9-]*$/;
const SLUG_MAX_LEN = 128;

// TODO: per operator request, the routing settings need a deeper UX
// pass: surface lb_policy / weight_fn / failover thresholds, allow
// editing hash policies inline, expose envoy override hooks and
// preview the rendered Envoy snapshot. Tracking this in the next
// iteration; for now the page is read-only and edits go through the
// Service wizard.
export function ServiceRouting({ service, pools }: Props) {
  const sid = service.id;
  const hashPolicies = useQuery({
    queryKey: ["service", sid, "hash"],
    queryFn: () => api.get<HashPolicy[]>(`/services/${sid}/hash-policies`),
  });
  // Live per-pool locality weights drive Envoy's Layer-1 cascade. The
  // controller exposes them via `/services/{id}/locality-weights`
  // (read-only — operators tune SLOs, not the weights themselves).
  const weights = useQuery<Record<number, number>>({
    queryKey: ["service", sid, "locality-weights"],
    queryFn: () =>
      api.get<Record<number, number>>(`/services/${sid}/locality-weights`),
    staleTime: 2_000,
    refetchInterval: 5_000,
  });

  return (
    <>
      <EndpointNameSection service={service} />
      <DispatchSection
        service={service}
        pools={pools}
        hashPolicyCount={hashPolicies.data?.length ?? 0}
      />
      <LocalityCascadeSection
        pools={pools}
        weights={weights.data ?? {}}
        fill={service.dispatch_preference === "fill"}
      />

      <WsSubsetSection />

      <Section title="Routing defaults" icon={<Hash />}>
        <div className="kv">
          <span className="k">default_threshold_ms</span>
          <span className="v mono">{service.default_threshold_ms}</span>
          <span className="k">default_hash_key</span>
          <span className="v mono">{service.default_hash_key ?? "—"}</span>
        </div>
      </Section>

      <Section
        title="Hash policies"
        icon={<Hash />}
        count={`${hashPolicies.data?.length ?? 0}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Kind</th>
                <th>Name</th>
                <th>Terminal</th>
                <th style={{ textAlign: "right" }}>Order</th>
              </tr>
            </thead>
            <tbody>
              {(hashPolicies.data ?? []).map((h) => (
                <tr key={h.id}>
                  <td>
                    <Pill tone="info">{h.key_kind}</Pill>
                  </td>
                  <td className="mono">{h.key_name}</td>
                  <td className="mono">{String(h.terminal)}</td>
                  <td className="num">{h.order}</td>
                </tr>
              ))}
              {!hashPolicies.data?.length && (
                <tr>
                  <td colSpan={4} className="empty">
                    no hash policies — set them in the service wizard
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

/**
 * Editor for the service's dispatch mode — the two orthogonal axes
 * (`dispatch_preference` × `dispatch_on_full`) plus the spill factor.
 * Uses the ServiceAutoscaling save pattern: local draft seeded from
 * the service row, PATCH on save, invalidate ["services"] on success.
 * The DispatchEditor child enforces the one invalid pair (fill ×
 * spill) client-side; the controller re-validates against the merged
 * row on PATCH, so a raced edit still 422s with a readable message.
 */
function DispatchSection({
  service,
  pools,
  hashPolicyCount,
}: {
  service: Service;
  pools: Pool[];
  hashPolicyCount: number;
}) {
  const queryClient = useQueryClient();
  const fromService = (): DispatchValue => ({
    preference: service.dispatch_preference ?? "sticky",
    on_full: service.dispatch_on_full ?? "queue",
    spill_factor: service.dispatch_spill_factor ?? 150,
  });
  const [draft, setDraft] = useState<DispatchValue>(fromService);

  useEffect(() => {
    setDraft(fromService());
  }, [service.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persisted = fromService();
  const dirty =
    draft.preference !== persisted.preference ||
    draft.on_full !== persisted.on_full ||
    draft.spill_factor !== persisted.spill_factor;
  const spillFactorValid =
    Number.isFinite(draft.spill_factor) &&
    draft.spill_factor >= 100 &&
    draft.spill_factor <= 1000;

  const save = useMutation({
    mutationFn: () =>
      api.patch<Service>(`/services/${service.id}`, {
        dispatch_preference: draft.preference,
        dispatch_on_full: draft.on_full,
        dispatch_spill_factor: draft.spill_factor,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
    },
  });

  return (
    <Section
      title="Dispatch mode"
      icon={<Cable />}
      count={<Pill tone="brand">{dispatchModeName(draft)}</Pill>}
      actions={
        <>
          {dirty && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setDraft(fromService());
                save.reset();
              }}
            >
              <X />
              discard
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => save.mutate()}
            disabled={!dirty || !spillFactorValid || save.isPending}
            title={
              !dirty
                ? "no changes to save"
                : !spillFactorValid
                  ? "spill factor must be between 100 and 1000"
                  : undefined
            }
          >
            {save.isPending ? <Loader2 className="spin" /> : <Save />}
            save
          </button>
        </>
      }
    >
      <p className="muted" style={{ fontSize: 12, margin: "0 0 14px" }}>
        Two independent choices decide how Envoy hands each request to a
        replica: <strong>who serves it</strong> (preference) and{" "}
        <strong>what happens when that pod is full</strong> (on full).
        Changes reach the data plane within ~5s of saving — no rollout
        needed, and switching back is just as instant.
      </p>
      <DispatchEditor
        value={draft}
        onChange={(next) => {
          setDraft(next);
          save.reset();
        }}
        disabled={save.isPending}
        pools={pools}
        hashPolicyCount={hashPolicyCount}
      />
      {!spillFactorValid && draft.on_full === "spill" && (
        <div className="flash flash--danger" style={{ marginTop: 12 }}>
          spill factor must be between 100 and 1000 (% of cluster-average
          load).
        </div>
      )}
      {save.isError && (
        <div className="flash flash--danger" style={{ marginTop: 12 }}>
          failed to save:{" "}
          <span className="mono">
            {save.error instanceof ApiError || save.error instanceof Error
              ? save.error.message
              : "unknown error"}
          </span>
        </div>
      )}
      {save.isSuccess && !dirty && (
        <div className="flash" style={{ marginTop: 12 }}>
          saved — Envoy picks the new mode up on the next snapshot push
          (≤5s).
        </div>
      )}
    </Section>
  );
}

/**
 * Read-only view of the per-pool Layer-1 weights that Envoy uses to
 * pick a pool (LocalityLbEndpoints). Under sticky dispatch the
 * autoscaler is the only writer — it spreads weight across priorities
 * (P0, P1, …) based on each pool's SLO headroom and capacity, so
 * seeing traffic on lower priorities is a normal steady state, not a
 * warning. Under fill dispatch the weights are the configured slot
 * ratio (capacity_per_replica), static by design. The point of this
 * panel is just to show, at a glance, what fraction of traffic each
 * pool is currently taking.
 */
function LocalityCascadeSection({
  pools,
  weights,
  fill,
}: {
  pools: Pool[];
  weights: Record<number, number>;
  fill: boolean;
}) {
  const rows: LocalityWeightRow[] = useMemo(
    () =>
      pools
        .map((p) => ({ pool: p, weight: weights[p.id] ?? 0 }))
        .sort((a, b) => a.pool.priority - b.pool.priority),
    [pools, weights],
  );
  const activeCount = useMemo(
    () => rows.filter(({ weight }) => weight > 0).length,
    [rows],
  );
  return (
    <Section
      title="Pool weight distribution (read-only)"
      icon={<Cable />}
      count={
        rows.length > 0 ? (
          <Pill tone="info">
            {activeCount}/{rows.length} active
          </Pill>
        ) : undefined
      }
    >
      <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
        {fill ? (
          <>
            Fill dispatch: each pool's weight is its configured slot ratio
            (per-replica <code>capacity_per_replica</code>, largest pool =
            1000) — static by design, every pool serves at once. Envoy then
            picks the replica with the largest free-slot share inside the
            pool split.
          </>
        ) : (
          <>
            Layer-1 weights — Envoy uses
            <code> LocalityLbEndpoints.load_balancing_weight</code> to pick a
            pool, then Maglev over the hash policies to pick a replica. The
            autoscaler distributes weight across priorities based on each
            pool's SLO headroom and capacity, so a multi-pool split is the
            normal steady state whenever the workload doesn't fit a single
            pool.
          </>
        )}
      </p>
      <LocalityWeightBar rows={rows} />
    </Section>
  );
}

/**
 * Read-only explainer for how the WS-aware Layer-2 subset routing is
 * wired. There's nothing to edit — the snapshot builder synthesises
 * the subset selectors and metadata automatically — but operators
 * routinely ask "what happens to my WebSockets when a pod drains?"
 * so we spell it out here.
 */
function WsSubsetSection() {
  return (
    <Section title="WebSocket-aware subset" icon={<Waves />}>
      <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
        Long-lived (WebSocket) connections need a different routing path
        from short-lived requests during scale-down. The snapshot builder
        publishes a subset selector keyed on the
        <code> accepts_long_lived</code> endpoint metadata; the WS
        upgrade route requires <code>accepts_long_lived: true</code>.
      </p>
      <div className="kv">
        <span className="k">subset selector</span>
        <span className="v mono">{`{ keys: [accepts_long_lived] }`}</span>
        <span className="k">WS upgrade route</span>
        <span className="v mono">
          {`metadata_match: { accepts_long_lived: true }`}
        </span>
        <span className="k">short-lived route</span>
        <span className="v mono">no metadata_match (any subset)</span>
        <span className="k">draining_long_lived</span>
        <span className="v">
          <Pill tone="info">accepts_long_lived = false</Pill>
          <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
            new WebSockets routed away; short-lived still flow
          </span>
        </span>
        <span className="k">draining</span>
        <span className="v">
          <Pill tone="warn">removed from EDS</Pill>
          <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
            no new traffic; in-flight short-lived drains
          </span>
        </span>
      </div>
    </Section>
  );
}

/**
 * Rename UI for the routing-key slug (`Service.name`).
 *
 * This is *deliberately* harder to reach than the cosmetic pencil
 * rename in the SubNav title (which only edits `display_name`). The
 * slug is what Envoy keys both the path-prefix routes and the per-host
 * VirtualHosts on — the externally visible API endpoint — so changing
 * it has real production impact:
 *
 *   - in-flight callers using `/<old-name>/...` on the path or sending
 *     `Host: <old-name>` on the header will start 404'ing once the next
 *     xDS snapshot lands;
 *   - subdomain DNS / managed-cert pinned to ``<old-name>.*`` no longer
 *     matches and needs separate Terraform updates;
 *   - the SkyServe service names on each Pool stay put — that side is
 *     keyed on `pool.sky_service_name`, independent of `service.name`,
 *     so replicas don't churn.
 *
 * The flow is therefore: collapsed by default → "Edit endpoint name…"
 * reveals a draft input behind a danger-toned warning banner → Save
 * commits and refreshes the services cache. The pencil rename in the
 * SubNav stays exactly as-is and remains cosmetic-only.
 */
function EndpointNameSection({ service }: { service: Service }) {
  // The capacity bank is a controller-owned singleton with no external
  // endpoint at all (it is excluded from routing), and the API refuses
  // to rename it — so don't offer the affordance.
  if (service.kind === "bank") return null;
  return <EndpointNameEditor service={service} />;
}

function EndpointNameEditor({ service }: { service: Service }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(service.name);
  const [acknowledged, setAcknowledged] = useState(false);

  // Re-seed the draft when the upstream slug changes (e.g. another
  // operator renamed it in a different tab). Don't clobber an in-flight
  // edit — that would erase what the user is currently typing.
  useEffect(() => {
    if (!editing) setDraft(service.name);
  }, [service.name, editing]);

  const rename = useMutation({
    mutationFn: async (next: string) => {
      await api.patch<Service>(`/services/${service.id}`, { name: next });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setEditing(false);
      setAcknowledged(false);
    },
  });

  const trimmed = draft.trim();
  const unchanged = trimmed === service.name;
  const tooLong = trimmed.length > SLUG_MAX_LEN;
  const wellFormed = SLUG_REGEX.test(trimmed) && !tooLong;
  const valid = wellFormed && !unchanged;

  let errorMessage: string | null = null;
  if (rename.isError) {
    if (rename.error instanceof ApiError && rename.error.status === 409) {
      errorMessage = `Another service already uses "${trimmed}". Pick a different slug.`;
    } else if (
      rename.error instanceof ApiError &&
      rename.error.status === 422
    ) {
      errorMessage =
        "The controller rejected this slug — must be lowercase letters, digits, or hyphens, starting with a letter.";
    } else {
      errorMessage =
        rename.error instanceof Error
          ? rename.error.message
          : "Rename failed.";
    }
  }

  const cancel = () => {
    setEditing(false);
    setDraft(service.name);
    setAcknowledged(false);
    rename.reset();
  };

  return (
    <Section
      title="Endpoint name"
      icon={<Globe />}
      count={<Pill tone="brand">routing key</Pill>}
      actions={
        !editing ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setEditing(true)}
          >
            <Pencil />
            Edit endpoint name…
          </button>
        ) : undefined
      }
    >
      <div className="kv" style={{ marginBottom: editing ? 12 : 0 }}>
        <span className="k">slug</span>
        <span className="v mono">{service.name}</span>
        <span className="k">caller path prefix</span>
        <span className="v mono">/{service.name}/…</span>
        <span className="k">Envoy VirtualHost</span>
        <span className="v mono">
          {service.name}, {service.name}.*
        </span>
        <span className="k">alt: caller Host header</span>
        <span className="v mono">Host: {service.name}</span>
      </div>

      {!editing && (
        <>
          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            This slug is the routing key Envoy uses for path-based dispatch
            (<code>/{service.name}/…</code>) <em>and</em> Host-header
            dispatch (<code>Host: {service.name}</code>) — both reach the
            same upstream. The pencil at the top of the page only edits the
            cosmetic <em>display name</em>; changing the slug itself
            happens here, behind a warning, because it affects every caller
            in production.
          </p>
          <ExampleUrls service={service} />
        </>
      )}

      {editing && (
        <>
          <div className="flash flash--danger" style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <AlertTriangle
                style={{ flex: "0 0 auto", marginTop: 1 }}
                size={16}
              />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  You are about to rename the API endpoint.
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    lineHeight: 1.55,
                  }}
                >
                  <li>
                    Callers using <code>/{service.name}/…</code> on the
                    path or <code>Host: {service.name}</code> on the header
                    will start receiving <code>404 no service</code> as
                    soon as the next xDS snapshot lands (seconds, not
                    minutes).
                  </li>
                  <li>
                    Subdomain clients pinned to{" "}
                    <code>{service.name}.&lt;ingress&gt;</code> stop
                    matching the VirtualHost. DNS records and the managed
                    SSL cert in <code>deploy/terraform/load_balancing.tf</code>{" "}
                    must be updated separately — backstage does not touch
                    those.
                  </li>
                  <li>
                    The Envoy cluster <code>{service.name}</code> is rebuilt
                    under the new slug. Existing connections drop and
                    reconnect on the new cluster.
                  </li>
                  <li>
                    The SkyServe service names on each pool
                    (<code>pool.sky_service_name</code>) are independent
                    and <strong>do not</strong> change — replicas keep
                    running.
                  </li>
                </ul>
                <label
                  className="muted"
                  style={{
                    display: "inline-flex",
                    gap: 6,
                    alignItems: "center",
                    fontSize: 12,
                    marginTop: 10,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                  />
                  I understand the impact above and want to rename the
                  endpoint.
                </label>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              className="mono"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={service.name}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              style={{ flex: "1 1 240px", minWidth: 200 }}
              aria-label="new endpoint slug"
            />
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => rename.mutate(trimmed)}
              disabled={!valid || !acknowledged || rename.isPending}
              title={
                !acknowledged
                  ? "Tick the impact-acknowledged box first"
                  : !wellFormed
                    ? "Slug must match ^[a-z][a-z0-9-]*$ and be ≤128 chars"
                    : unchanged
                      ? "New slug is identical to the current one"
                      : undefined
              }
            >
              {rename.isPending ? <Loader2 /> : <Save />}
              {rename.isPending ? "renaming…" : "Rename endpoint"}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={cancel}
              disabled={rename.isPending}
            >
              <X />
              cancel
            </button>
          </div>

          {!wellFormed && trimmed.length > 0 && (
            <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>
              Slug must be lowercase, start with a letter, contain only{" "}
              <code>a-z</code> / <code>0-9</code> / <code>-</code>, and be
              at most {SLUG_MAX_LEN} characters.
            </div>
          )}

          {errorMessage && (
            <div
              className="flash flash--warn"
              style={{ marginTop: 12, marginBottom: 0 }}
            >
              <AlertTriangle
                style={{
                  width: 14,
                  height: 14,
                  verticalAlign: "middle",
                  marginRight: 6,
                }}
              />
              {errorMessage}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

/**
 * Concrete, copy-pasteable curl examples for the current Service slug.
 *
 * The WebUI is served by the same Envoy ingress that handles LLM Service
 * traffic (see `_admin_virtual_host` and the per-service path-prefix
 * routes in `controller/controller/routing/snapshot_builder.py`). That
 * means `window.location` already points at the right base URL — the
 * three forms below differ only in how Envoy is asked to dispatch:
 * path-prefix (default), Host header, or subdomain. We derive examples
 * from the current location instead of hard-coding
 * `backstage-<env>.spiral-ai-app.com`, so dev1 / prod / future envs all
 * Just Work without a controller round-trip.
 *
 * Local-dev caveat: when running `npm run dev`, `window.location` is
 * Vite (`localhost:5173`), which does *not* run Envoy at all. The
 * examples in that case are still informative as a template — operators
 * editing locally aren't the audience for "actually-callable URLs".
 */
function ExampleUrls({ service }: { service: Service }) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const examples = useMemo(() => {
    const proto =
      typeof window !== "undefined" && window.location.protocol === "http:"
        ? "http"
        : "https";
    const ingress =
      typeof window !== "undefined" && window.location.host
        ? window.location.host
        : "<ingress-host>";
    const slug = service.name;

    type Example = {
      id: string;
      label: string;
      curl: string;
      hint: ReactNode;
    };

    const list: Example[] = [
      {
        id: "path-prefix",
        label: "Path prefix (recommended — works on any environment)",
        curl: `curl -i ${proto}://${ingress}/${slug}/healthz`,
        hint: (
          <>
            Envoy strips the <code>/{slug}/</code> prefix before
            forwarding, so upstreams (vLLM, TGI, echo containers) see the
            same paths they would via the Host-header form. No DNS or
            cert work needed; same hostname the WebUI lives on. Always
            include the trailing slash — bare <code>/{slug}</code> falls
            through to the next route.
          </>
        ),
      },
      {
        id: "host-header",
        label: "Host header (alternate — no path rewrite)",
        curl: `curl -i ${proto}://${ingress}/healthz \\\n  -H 'Host: ${slug}'`,
        hint: (
          <>
            URL hostname is the ingress; Envoy keys on the{" "}
            <code>Host</code> header and forwards the path verbatim.
            Useful when you can't (or don't want to) prepend a prefix to
            the upstream path.
          </>
        ),
      },
      {
        id: "subdomain",
        label: "Subdomain (needs DNS + managed cert)",
        curl: `curl -i ${proto}://${slug}.${ingress}/healthz`,
        hint: (
          <>
            Requires <code className="mono">{`${slug}.${ingress}`}</code>{" "}
            to resolve to the ingress IP and to be listed in the managed
            SSL cert (<code>deploy/terraform/load_balancing.tf</code>).
          </>
        ),
      },
    ];

    return list;
  }, [service.name]);

  async function copy(id: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      window.setTimeout(() => {
        setCopiedId((cur) => (cur === id ? null : cur));
      }, 1500);
    } catch {
      // Clipboard write can be denied (focus / permissions / insecure
      // context). Fall back to a prompt so the operator can still grab
      // the command via Cmd-C.
      window.prompt("Copy curl command:", text);
    }
  }

  return (
    <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--ink-faint)",
        }}
      >
        Example URLs
      </div>
      {examples.map((ex) => (
        <div key={ex.id}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              {ex.label}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => copy(ex.id, ex.curl)}
              title="Copy curl command to clipboard"
            >
              {copiedId === ex.id ? <Check /> : <Clipboard />}
              {copiedId === ex.id ? "copied" : "copy"}
            </button>
          </div>
          <pre className="code">{ex.curl}</pre>
          <p
            className="muted"
            style={{ fontSize: 11.5, margin: "6px 0 0", lineHeight: 1.5 }}
          >
            {ex.hint}
          </p>
        </div>
      ))}
    </div>
  );
}
